import type { SyntheticEvent } from "react";
import { preloadRouteForPath } from "./lazyRoutes";

/**
 * Hover/focus prefetch for the sidebar.
 *
 * PrimeReact menu items only expose their target through the `command` callback, so
 * each sidebar item carries its route as a class-name marker (`routeClass(path)`).
 * One delegated listener on the sidebar root reads that marker back and warms the
 * route's chunk before the user actually clicks.
 */

const CLASS_PREFIX = "sb-route-";

/** Class-name-safe encoding of a route path (reversible, see routeFromClassName). */
export const routeClass = (path: string): string =>
	CLASS_PREFIX + path.replace(/[^A-Za-z0-9-]/g, (char) => `_${char.charCodeAt(0).toString(16)}_`);

export const routeFromClassName = (className: string): string | null => {
	const token = className.split(/\s+/).find((part) => part.startsWith(CLASS_PREFIX));
	if (!token) {
		return null;
	}
	return token
		.slice(CLASS_PREFIX.length)
		.replace(/_([0-9a-f]+)_/g, (_match, hex: string) => String.fromCharCode(parseInt(hex, 16)));
};

/**
 * Hover intent: only prefetch when the pointer rests on an item for a moment.
 * Sweeping the mouse across the sidebar (or through an opened sub-menu) must not
 * fire a burst of chunk requests.
 */
const HOVER_INTENT_MS = 150;
let hoverTimer: ReturnType<typeof setTimeout> | null = null;
let hoverPath: string | null = null;

/** Attach to the sidebar root as `onMouseOver` and `onFocusCapture`. */
export const prefetchRouteFromEvent = (event: SyntheticEvent<HTMLElement>): void => {
	const target = event.target as HTMLElement | null;
	const marked = target?.closest?.(`[class*="${CLASS_PREFIX}"]`) as HTMLElement | null;
	if (!marked) {
		return;
	}
	const path = routeFromClassName(marked.className);
	if (!path || path === hoverPath) {
		return;
	}
	hoverPath = path;
	if (hoverTimer) {
		clearTimeout(hoverTimer);
	}
	hoverTimer = setTimeout(() => {
		hoverTimer = null;
		void preloadRouteForPath(path);
	}, HOVER_INTENT_MS);
};
